/**
 * TMDashboardPage — overview stats + recent tasks
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckSquare, Clock, AlertCircle, TrendingUp, Plus } from 'lucide-react';
import { useTMAuth } from '@/contexts/TMAuthContext';
import { getAllTasks, getMyTasks } from '@/services/tmService';
import type { Task, TaskStatus } from '@/types/tm.types';
import { TASK_STATUS_COLORS, TASK_STATUS_LABELS, TASK_PRIORITY_COLORS, TASK_PRIORITY_LABELS } from '@/types/tm.types';
import { cn } from '@/utils/cn';

function daysUntil(date: Date | null): number | null {
  if (!date) return null;
  return Math.ceil((date.getTime() - Date.now()) / 86400000);
}

export default function TMDashboardPage() {
  const { tmUser, isTMAdmin } = useTMAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tmUser) return;
    const load = async () => {
      const data = isTMAdmin
        ? await getAllTasks()
        : await getMyTasks(tmUser.uid);
      setTasks(data);
      setLoading(false);
    };
    load();
  }, [tmUser, isTMAdmin]);

  const counts: Record<TaskStatus, number> = {
    todo:        tasks.filter(t => t.status === 'todo').length,
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    review:      tasks.filter(t => t.status === 'review').length,
    done:        tasks.filter(t => t.status === 'done').length,
  };

  const overdue  = tasks.filter(t => t.dueDate && daysUntil(t.dueDate)! < 0 && t.status !== 'done');
  const upcoming = tasks.filter(t => {
    const d = daysUntil(t.dueDate);
    return d !== null && d >= 0 && d <= 3 && t.status !== 'done';
  });
  const recent = tasks.slice(0, 5);

  const stats = [
    { label: 'To Do',       value: counts.todo,        icon: CheckSquare, color: 'bg-gray-100 text-gray-600' },
    { label: 'In Progress', value: counts.in_progress, icon: TrendingUp,  color: 'bg-amber-100 text-amber-600' },
    { label: 'Review',      value: counts.review,      icon: Clock,       color: 'bg-blue-100 text-blue-600' },
    { label: 'Done',        value: counts.done,        icon: CheckSquare, color: 'bg-emerald-100 text-emerald-600' },
  ];

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            Hello, {tmUser?.name.split(' ')[0]} 👋
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isTMAdmin ? 'All tasks' : 'Your tasks'} overview
          </p>
        </div>
        {isTMAdmin && (
          <Link to="/tm/tasks/new"
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold active:scale-95 transition-all">
            <Plus className="h-4 w-4" />
            New Task
          </Link>
        )}
      </div>

      {/* Stats grid */}
      {loading ? (
        <div className="grid grid-cols-2 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-20 rounded-2xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {stats.map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
              <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center shrink-0', color)}>
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 leading-none">{value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Overdue alert */}
      {overdue.length > 0 && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-2xl p-4">
          <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-700">{overdue.length} overdue task{overdue.length !== 1 ? 's' : ''}</p>
            <p className="text-xs text-red-500 mt-0.5">Needs immediate attention</p>
          </div>
        </div>
      )}

      {/* Due soon */}
      {upcoming.length > 0 && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <Clock className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-700">{upcoming.length} task{upcoming.length !== 1 ? 's' : ''} due within 3 days</p>
            <p className="text-xs text-amber-500 mt-0.5">Stay on track</p>
          </div>
        </div>
      )}

      {/* Recent tasks */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-700">Recent Tasks</h2>
          <Link to="/tm/tasks" className="text-xs text-indigo-600 font-medium">View all</Link>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 rounded-2xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : recent.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm bg-white rounded-2xl border border-gray-100">
            No tasks yet.{isTMAdmin && ' Create one to get started.'}
          </div>
        ) : (
          <div className="space-y-2">
            {recent.map(task => {
              const d = daysUntil(task.dueDate);
              return (
                <Link key={task.id} to={`/tm/tasks/${task.id}`}
                  className="block bg-white rounded-2xl border border-gray-100 shadow-sm p-4 active:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-gray-900 leading-snug line-clamp-1">{task.title}</p>
                    <span className={cn('shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full', TASK_STATUS_COLORS[task.status])}>
                      {TASK_STATUS_LABELS[task.status]}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <span className={cn('text-[10px] font-medium px-2 py-0.5 rounded-full', TASK_PRIORITY_COLORS[task.priority])}>
                      {TASK_PRIORITY_LABELS[task.priority]}
                    </span>
                    {d !== null && (
                      <span className={cn('text-[10px]', d < 0 ? 'text-red-500 font-semibold' : d <= 3 ? 'text-amber-600 font-medium' : 'text-gray-400')}>
                        {d < 0 ? `${Math.abs(d)}d overdue` : d === 0 ? 'Due today' : `${d}d left`}
                      </span>
                    )}
                    {task.assignedTo && (
                      <span className="text-[10px] text-gray-400 ml-auto">→ {task.assignedTo.name.split(' ')[0]}</span>
                    )}
                  </div>
                  {/* Progress bar */}
                  {task.progress > 0 && (
                    <div className="mt-2.5 h-1 rounded-full bg-gray-100">
                      <div className="h-1 rounded-full bg-indigo-400 transition-all" style={{ width: `${task.progress}%` }} />
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
